import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

type AdminRole = 'superadmin' | 'admin' | 'moderator' | 'viewer';

interface AdminUser {
  id: string;
  user_id: string;
  email: string;
  display_name: string | null;
  role: AdminRole;
  is_active: boolean;
  last_login_at: string | null;
  login_count: number;
}

interface AdminAuthContextType {
  user: User | null;
  adminUser: AdminUser | null;
  isLoading: boolean;
  isAdmin: boolean;
  hasRole: (minRole: AdminRole) => boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshAdminUser: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

const ROLE_HIERARCHY: Record<AdminRole, number> = {
  viewer: 1,
  moderator: 2,
  admin: 3,
  superadmin: 4,
};

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAdminUser = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        console.error('Error fetching admin user:', error);
        return null;
      }

      if (data) {
        // Check if locked
        if (data.locked_until && new Date(data.locked_until) > new Date()) {
          console.warn('Admin account is locked');
          return null;
        }
        return data as AdminUser;
      }
      return null;
    } catch (err) {
      console.error('Error in fetchAdminUser:', err);
      return null;
    }
  }, []);

  const refreshAdminUser = useCallback(async () => {
    if (user) {
      const admin = await fetchAdminUser(user.id);
      setAdminUser(admin);
    }
  }, [user, fetchAdminUser]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Use setTimeout to avoid potential race conditions
        setTimeout(async () => {
          const admin = await fetchAdminUser(session.user.id);
          setAdminUser(admin);
          setIsLoading(false);
        }, 0);
      } else {
        setAdminUser(null);
        setIsLoading(false);
      }
    });

    // THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setUser(session?.user ?? null);
      
      if (session?.user) {
        const admin = await fetchAdminUser(session.user.id);
        setAdminUser(admin);
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchAdminUser]);

  const signIn = async (email: string, password: string): Promise<{ error: string | null }> => {
    try {
      // Use supabase-js invoke so required headers (apikey + Authorization) are always correct.
      const { data, error } = await supabase.functions.invoke('admin-login', {
        body: { email, password },
      });

      if (error) {
        return { error: error.message || 'Login failed' };
      }

      const payload = data as any;
      const session = payload?.session;
      const admin = payload?.admin_user as AdminUser | undefined;

      if (session?.access_token && session?.refresh_token) {
        await supabase.auth.setSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
        });
      } else {
        return { error: 'Login failed: missing session.' };
      }

      // Set admin user immediately from the edge function response to avoid any client-side RLS issues.
      if (admin) {
        setAdminUser(admin);
      } else {
        // Fallback to client-side fetch (should normally not be needed)
        const currentUserId = payload?.user?.id;
        if (currentUserId) {
          const fetched = await fetchAdminUser(currentUserId);
          setAdminUser(fetched);
        }
      }

      return { error: null };
    } catch (err) {
      console.error('Sign in error:', err);
      return { error: 'An unexpected error occurred.' };
    }
  };

  const signOut = async () => {
    if (adminUser) {
      await supabase.from('admin_security_events').insert({
        admin_user_id: adminUser.id,
        event_type: 'logout',
        success: true,
      });
    }
    await supabase.auth.signOut();
    setAdminUser(null);
  };

  const hasRole = (minRole: AdminRole): boolean => {
    if (!adminUser) return false;
    return ROLE_HIERARCHY[adminUser.role] >= ROLE_HIERARCHY[minRole];
  };

  const value: AdminAuthContextType = {
    user,
    adminUser,
    isLoading,
    isAdmin: !!adminUser,
    hasRole,
    signIn,
    signOut,
    refreshAdminUser,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}
